import type { ButtonHTMLAttributes, ReactNode } from 'react'

type NativeButton = ButtonHTMLAttributes<HTMLButtonElement>

/**
 * De terracotta pill. Op 390px is hij 60px hoog en vult hij de breedte; op
 * desktop 64px met een maximum, precies zoals ronde 2 en 3 het laten zien.
 *
 * Uitgeschakeld gaat hij naar --color-inert, en dat is de enige plek waar die
 * kleur voorkomt.
 */
export function PrimaryButton({
  children,
  className = '',
  ...rest
}: NativeButton & { children: ReactNode }) {
  return (
    <button
      type="button"
      {...rest}
      className={
        'flex h-15 w-full items-center justify-center gap-2.5 rounded-full text-[17px] ' +
        'font-semibold transition-transform sm:h-16 sm:text-[18px] ' +
        'bg-accent text-accent-ink shadow-accent active:scale-[0.99] ' +
        'disabled:bg-inert disabled:text-inert-ink disabled:shadow-none disabled:active:scale-100 ' +
        className
      }
    >
      {children}
    </button>
  )
}

/** Dezelfde maat, maar als omlijnde variant: "Terug naar de galerij". */
export function SecondaryButton({
  children,
  className = '',
  ...rest
}: NativeButton & { children: ReactNode }) {
  return (
    <button
      type="button"
      {...rest}
      className={
        'border-line flex h-14 w-full items-center justify-center rounded-full border ' +
        'text-ink-muted text-base font-medium transition-transform active:scale-[0.99] ' +
        className
      }
    >
      {children}
    </button>
  )
}

/**
 * De ronde knop met één teken erin: de pijl terug, het kruisje, de ⋯.
 * `tone` schakelt tussen de lichte schermen en de donkere viewer.
 */
export function IconButton({
  children,
  label,
  tone = 'light',
  className = '',
  ...rest
}: NativeButton & { children: ReactNode; label: string; tone?: 'light' | 'dark' }) {
  const tones = {
    light: 'border border-line text-ink-muted',
    dark: 'bg-viewer-ink/15 text-viewer-ink',
  }

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      className={
        'flex size-10 shrink-0 items-center justify-center rounded-full text-[17px] ' +
        'transition-transform active:scale-95 ' +
        tones[tone] +
        ' ' +
        className
      }
    >
      {children}
    </button>
  )
}
